import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccessTime as ActiveIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';

const timeAgo = (iso) => {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  if (diff < 0) return 'Just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon}mo ago`;
  return `${Math.floor(mon / 12)}y ago`;
};

const formatExact = (iso) => {
  if (!iso) return 'Never active';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// "Active in the last 15 minutes" reads as currently online.
const isOnline = (iso) => {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 15 * 60 * 1000;
};

const LastActive = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getLastActiveUsers({ search: search || undefined, page, size: rowsPerPage });
      const pageData = res?.data?.data || {};
      const content = pageData.content || [];
      setRows(content);
      setTotalElements(pageData.total_elements ?? content.length);
    } catch (err) {
      console.error('Error fetching last-active users:', err);
      setError('Failed to load activity');
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ActiveIcon sx={{ color: 'secondary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
            Last Active
          </Typography>
          <Chip
            label={`${totalElements} Typing Centers`}
            size="small"
            sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 24 }}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading} size="small">
            <RefreshIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by typing center name or code…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          sx={{
            flex: 1,
            minWidth: 240,
            '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: '8px', height: 36 },
            '& .MuiInputBase-input': { fontSize: '0.85rem' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        {search && (
          <Tooltip title="Clear search">
            <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(0); }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 1.5 }}>{error}</Typography>
      )}

      <Paper sx={{ borderRadius: '10px', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: isDark ? 'rgba(60,174,163,0.12)' : '#f0f7f7', color: 'text.primary', fontWeight: 700, fontSize: '0.8rem' } }}>
                <TableCell>Typing Center</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Last Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No typing centers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={isOnline(row.last_active_at) ? 'Active in the last 15 minutes' : 'Idle'}>
                          <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: isOnline(row.last_active_at) ? 'success.main' : 'text.disabled',
                            flexShrink: 0
                          }} />
                        </Tooltip>
                        {row.name || '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{row.code || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={formatExact(row.last_active_at)}>
                        <Typography sx={{ fontSize: '0.82rem', color: row.last_active_at ? 'text.primary' : 'text.disabled' }}>
                          {timeAgo(row.last_active_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default LastActive;
